import React, { useEffect, useState } from 'react';

/**
 * AttachmentChip — a single email attachment, clickable when downloadable.
 *
 * Resolves a download URL in this order:
 *   1. `attachment.downloadUrl` — incoming mails, set by the fetcher (token URL).
 *   2. `attachment.storagePath` — outgoing mails uploaded by the composer;
 *      resolved on the fly via Firebase Storage's getDownloadURL.
 *
 * If neither is available (legacy incoming mails fetched before attachments
 * were persisted), the chip renders as plain, non-clickable text.
 */
export default function AttachmentChip({ attachment }) {
  const [url, setUrl] = useState(attachment.downloadUrl || null);

  useEffect(() => {
    let cancelled = false;

    if (attachment.downloadUrl) {
      setUrl(attachment.downloadUrl);
      return undefined;
    }
    if (!attachment.storagePath) {
      setUrl(null);
      return undefined;
    }

    (async () => {
      try {
        const { getStorage, ref: storageRef, getDownloadURL } = await import('firebase/storage');
        const resolved = await getDownloadURL(storageRef(getStorage(), attachment.storagePath));
        if (!cancelled) setUrl(resolved);
      } catch (err) {
        if (!cancelled) setUrl(null);
      }
    })();

    return () => { cancelled = true; };
  }, [attachment.downloadUrl, attachment.storagePath]);

  const sizeLabel = attachment.size ? `(${Math.round(attachment.size / 1024)}KB)` : '';
  const name = attachment.filename || 'Anhang';

  if (!url) {
    return (
      <span className="ivx-attachment-chip" title="Datei nicht mehr verfügbar">
        📎 {name} {sizeLabel}
      </span>
    );
  }

  return (
    <a
      className="ivx-attachment-chip ivx-attachment-chip-link"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      download={name}
      title={`${name} öffnen / herunterladen`}
    >
      📎 {name} {sizeLabel}
    </a>
  );
}
